// * EXTERNAL IMPORTS
import { useActionData, useLocation, useOutletContext } from "@remix-run/react";
import { AlertProps, Collapse, Text, VStack, Link } from "@chakra-ui/react";
import { withZod } from "@remix-validated-form/with-zod";
import { ValidatedForm } from "remix-validated-form";
import { z } from "zod";
import { ActionArgs } from "@remix-run/node";
import { redirect, typedjson, TypedJsonResponse } from "remix-typedjson";
import Mixpanel from "mixpanel";

// * INTERNAL IMPORTS
import ValidatedInput from "components/ValidatedInput";
import { ValidatedButton } from "components/ValidatedSubmitButton";
import { TypedActionResponse } from "types/custom_types";
import Alert from "components/Alert";
import getSupabaseServerClient from "lib/supabase.server";
import { ValidatedCheckbox } from "components/ValidatedCheckbox";
import { useEffect, useRef, useState } from "react";
import mixpanel from "mixpanel-browser";
import ValidatedPassword from "~/components/ValidatedPassword";
import { SupabaseOutletContext } from "~/root";
import { GoogleSigninButton } from "../components/GoogleSigninButton";
import { AuthLayout } from "~/pages/AuthLayout";
import { FooterLink } from "~/components/AuthFooterLink";
import { AuthFieldsWrapper } from "~/components/AuthFieldsWrapper";
import { AuthActionWrapper } from "~/components/AuthActionWrapper";
import { AuthContinueDivider } from "~/components/AuthContinueDivider";

export const meta = () => {
  return [{ charset: "utf-8" }, { title: "Signup | CopyFrame" }, { viewport: "width=device-width,initial-scale=1" }];
};

// * LOADER
export const loader = async ({ request }: ActionArgs) => {
  const response = new Response();
  const supabase = getSupabaseServerClient({ request, response });
  const { data: sessionData, error: errorData } = await supabase.auth.getSession();
  if (errorData) return typedjson({ type: "error", message: "You are not logged in" }, { status: 401 });

  if (sessionData.session?.user.id) return redirect("/drive", { headers: response.headers });

  return typedjson({ type: "success" });
};

// * ROUTE COMPONENT
export default function SignupRoute() {
  const actionData = useActionData<TypedActionResponse>();
  const { supabase } = useOutletContext<SupabaseOutletContext>();

  const [termsAgreed, setTermsAgreed] = useState(false);

  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    // Send event to Mixpanel if this is a signup page view

    mixpanel.track("Page Viewed", { Pathname: "/signup", "Page Name": "Signup" });

    // Check if there's an error in the URL
    const hash = window.location.hash.substring(1); // Remove leading '#'
    const params = new URLSearchParams(hash);

    if (!params.has("error_description")) return; // No error to show

    // URLSearchParams won't work directly on the hash, so we need to manually find our value
    for (const [key, value] of params.entries()) {
      if (key === "error_description") {
        // Decoding to convert things like '%20' back to ' '
        setUrlError(decodeURIComponent(value));
        break; // No need to check further
      }
    }
  }, []);

  const formId = "signup-form";

  const handleFormSubmit = (data: any) => {
    mixpanel.identify(data.email);
    mixpanel.track("Signed Up", { Provider: "Password" });
  };

  return (
    <AuthLayout
      title="Welcome to CopyFrame"
      subtitle="Let's get you set up with a free account!"
      bodyContent={
        <>
          <GoogleSigninButton label="Sign up with Google" redirectUrl="/drive" supabase={supabase} />
          <AuthContinueDivider text="or continue with email" />
          <ValidatedForm id={formId} method="post" validator={authValidator} action="/signup" onSubmit={(data: any) => handleFormSubmit(data)}>
            <AuthFieldsWrapper>
              <ValidatedInput name="email" label="Email address" type="email" formId={formId} onChange={() => setUrlError(null)} />
              <ValidatedPassword name="password" />
              <ValidatedCheckbox
                name="terms_agreed"
                size="md"
                formId={formId}
                my={2}
                label={
                  <Text fontSize="sm">
                    I agree to the{" "}
                    <Link
                      href="https://app.termly.io/document/terms-of-service/6a1ead56-3302-4447-92b2-c4d2011f171d"
                      color="gray.800"
                      textDecor="underline"
                      target="_blank">
                      Terms of Use
                    </Link>{" "}
                    and{" "}
                    <Link
                      href="https://app.termly.io/document/privacy-policy/576d8833-46a7-4b38-942a-a3d7c792464b"
                      color="gray.800"
                      textDecor="underline"
                      target="_blank">
                      Privacy Policy
                    </Link>
                  </Text>
                }
                value={termsAgreed}
                setValue={setTermsAgreed}
              />
              <AuthActionWrapper>
                <Collapse in={actionData?.type !== "success"}>
                  <ValidatedButton formId={formId} label="Create my free account" loadingText="Creating free account..." width="full" />
                </Collapse>
                <Collapse in={!!actionData && !!actionData?.message}>
                  <Alert status={actionData?.type as AlertProps["status"]} description={actionData?.message ?? ""} />
                </Collapse>
              </AuthActionWrapper>
            </AuthFieldsWrapper>
          </ValidatedForm>
        </>
      }
      footerContent={
        <>
          <FooterLink text="Already have an account?" linkText="Log in" href="/login" />
        </>
      }
    />
  );
}

// * VALIDATOR
const authValidator = withZod(
  z.object({
    email: z.string().email({ message: "Please enter a valid email address" }),
    password: z.string().regex(new RegExp("^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$"), {
      message: "Password must be at least 8 characters and contain an uppercase letter, a lowercase letter, and a number",
    }),
    terms_agreed: z.string().refine((value) => value === "true", { message: "You must agree to the terms and conditions" }),
  })
);

// * ACTION
export const action = async ({ request }: ActionArgs): Promise<TypedJsonResponse<TypedActionResponse>> => {
  const response = new Response();
  const formData = await request.formData();
  const supabase = getSupabaseServerClient({ request, response });
  const result = await authValidator.validate(formData);
  if (result.error) return typedjson({ data: result.error, message: "Validation failed", type: "error" }, { status: 400 });
  const { data: signInData, error: errorData } = await supabase.auth.signUp({
    email: result.data.email,
    password: result.data.password,
    options: {
      emailRedirectTo: `${process.env.SITE_URL}/onboarding`,
    },
  });
  if (errorData) return typedjson({ data: errorData, message: errorData.message, type: "error" }, { status: 400 });
  else {
    return redirect(`${process.env.SITE_URL}/onboarding`, { headers: response.headers });
  }
};
